import {
  InternalAuditComment,
  InternalAuditCriteriaApplyResult,
  InternalAuditCriteriaPreview,
  InternalAuditCriterionApplied,
  InternalAuditCriterionDraft,
  InternalAuditCriterionSkipped,
  InternalAuditImprovement,
  InternalAuditImprovementAnalysis,
  InternalAuditImprovementProposal,
  InternalAuditImprovementResult,
  InternalAuditUnmatchedTask,
} from 'models/internalAuditImprovement';
import {
  InternalAuditCommentDTO,
  InternalAuditCriteriaApplyResultDTO,
  InternalAuditCriteriaPreviewDTO,
  InternalAuditCriterionAppliedDTO,
  InternalAuditCriterionDraftDTO,
  InternalAuditCriterionSkippedDTO,
  InternalAuditImprovementAnalysisDTO,
  InternalAuditImprovementDTO,
  InternalAuditImprovementProposalDTO,
  InternalAuditImprovementResultDTO,
  InternalAuditUnmatchedTaskDTO,
} from 'models/dto/internalAuditImprovementDTO';
import { fromApiDateTimeOptional } from 'utils/datetime';

export function mapFromInternalAuditImprovement(dto: InternalAuditImprovementDTO): InternalAuditImprovement {
  const output = new InternalAuditImprovement();
  output.comments = (dto?.comments ?? []).map((item) => mapFromInternalAuditComment(item));
  output.unmatchedTasks = (dto?.unmatchedTasks ?? []).map((item) => mapFromInternalAuditUnmatchedTask(item));
  output.improvementTaskId = dto?.improvementTaskId ?? undefined;
  output.criteriaImprovedOn = fromApiDateTimeOptional(dto?.criteriaImprovedOn);

  return output;
}

// The API writes null for an absent anchor; the model wants undefined so `!== undefined` checks hold.
function mapFromInternalAuditComment(dto: InternalAuditCommentDTO): InternalAuditComment {
  const output = new InternalAuditComment();
  output.taskId = dto.taskId;
  output.taskName = dto.taskName ?? '';
  output.procedureTaskId = dto.procedureTaskId ?? undefined;
  output.procedureName = dto.procedureName ?? undefined;
  output.coveredItemNames = dto.coveredItemNames ?? [];
  output.kpiId = dto.kpiId;
  output.question = dto.question ?? '';
  output.comment = dto.comment ?? '';
  output.commentHtml = dto.commentHtml ?? '';
  output.entityType = dto.entityType ?? undefined;
  output.entityId = dto.entityId ?? undefined;
  output.criterionKey = dto.criterionKey ?? undefined;
  output.entityName = dto.entityName ?? undefined;

  return output;
}

function mapFromInternalAuditUnmatchedTask(dto: InternalAuditUnmatchedTaskDTO): InternalAuditUnmatchedTask {
  const output = new InternalAuditUnmatchedTask();
  output.taskId = dto.taskId;
  output.taskName = dto.taskName ?? '';
  output.description = dto.description ?? '';
  output.coveredItemNames = dto.coveredItemNames ?? [];
  output.procedureName = dto.procedureName ?? '';
  //the API writes null, not undefined, for an absent id: without the coalesce every task would read
  //as one that lands on an existing procedure
  output.existingProcedureTaskId = dto.existingProcedureTaskId ?? undefined;
  output.existingProcedureName = dto.existingProcedureName ?? undefined;
  output.descriptionDiffers = dto.descriptionDiffers === true;
  output.checkListDiffers = dto.checkListDiffers === true;

  return output;
}

export function mapFromInternalAuditImprovementAnalysis(
  dto: InternalAuditImprovementAnalysisDTO,
): InternalAuditImprovementAnalysis {
  const output = new InternalAuditImprovementAnalysis();
  output.proposals = (dto?.proposals ?? []).map((item) => mapFromInternalAuditImprovementProposal(item));
  output.nothingToImprove = dto?.nothingToImprove === true;

  return output;
}

function mapFromInternalAuditImprovementProposal(
  dto: InternalAuditImprovementProposalDTO,
): InternalAuditImprovementProposal {
  const output = new InternalAuditImprovementProposal();
  output.taskId = dto.taskId;
  output.kpiId = dto.kpiId;
  output.question = dto.question ?? '';
  output.comment = dto.comment ?? '';
  output.commentHtml = dto.commentHtml ?? '';
  output.procedureTaskId = dto.procedureTaskId ?? undefined;
  output.procedureName = dto.procedureName ?? undefined;
  output.entityType = dto.entityType ?? undefined;
  output.entityId = dto.entityId ?? undefined;
  output.criterionKey = dto.criterionKey ?? undefined;
  output.entityName = dto.entityName ?? undefined;
  output.evaluation = dto.evaluation ?? '';
  output.recommendation = dto.recommendation ?? '';

  return output;
}

// The proposals are posted back whole: the analysis is not stored server-side, so what the user
// picked travels with the request that acts on it. The server re-derives every identity from its own
// comments and reads only the evaluation and recommendation off this.
export function mapToInternalAuditImprovementProposal(
  proposal: InternalAuditImprovementProposal,
): InternalAuditImprovementProposalDTO {
  const output = new InternalAuditImprovementProposalDTO();
  output.taskId = proposal.taskId;
  output.kpiId = proposal.kpiId;
  output.question = proposal.question;
  output.comment = proposal.comment;
  output.commentHtml = proposal.commentHtml;
  output.procedureTaskId = proposal.procedureTaskId;
  output.procedureName = proposal.procedureName;
  output.entityType = proposal.entityType;
  output.entityId = proposal.entityId;
  output.criterionKey = proposal.criterionKey;
  output.entityName = proposal.entityName;
  output.evaluation = proposal.evaluation;
  output.recommendation = proposal.recommendation;

  return output;
}

export function mapFromInternalAuditCriteriaPreview(dto: InternalAuditCriteriaPreviewDTO): InternalAuditCriteriaPreview {
  const output = new InternalAuditCriteriaPreview();
  output.drafts = (dto?.drafts ?? []).map((item) => mapFromInternalAuditCriterionDraft(item));
  output.skipped = (dto?.skipped ?? []).map((item) => mapFromInternalAuditCriterionSkipped(item));

  return output;
}

function mapFromInternalAuditCriterionDraft(dto: InternalAuditCriterionDraftDTO): InternalAuditCriterionDraft {
  const output = new InternalAuditCriterionDraft();
  output.entityType = dto.entityType;
  output.entityId = dto.entityId;
  output.entityName = dto.entityName ?? '';
  output.criterionKey = dto.criterionKey;
  output.originalRequirement = dto.originalRequirement ?? '';
  output.originalSuccessCriteria = dto.originalSuccessCriteria ?? '';
  output.proposedRequirement = dto.proposedRequirement ?? '';
  output.proposedSuccessCriteria = dto.proposedSuccessCriteria ?? '';
  output.remarks = (dto.remarks ?? []).map((item) => mapFromInternalAuditImprovementProposal(item));
  output.include = true;

  return output;
}

function mapFromInternalAuditCriterionSkipped(dto: InternalAuditCriterionSkippedDTO): InternalAuditCriterionSkipped {
  const output = new InternalAuditCriterionSkipped();
  output.entityType = dto.entityType ?? undefined;
  output.entityId = dto.entityId ?? undefined;
  output.entityName = dto.entityName ?? undefined;
  output.criterionKey = dto.criterionKey ?? undefined;
  output.reason = dto.reason ?? '';
  output.remarks = (dto.remarks ?? []).map((item) => mapFromInternalAuditImprovementProposal(item));

  return output;
}

// The approved draft, texts as the user left them. The originals travel back with it: the server
// refuses the draft when the criterion no longer reads like that.
export function mapToInternalAuditCriterionDraft(draft: InternalAuditCriterionDraft): InternalAuditCriterionDraftDTO {
  const output = new InternalAuditCriterionDraftDTO();
  output.entityType = draft.entityType;
  output.entityId = draft.entityId;
  output.entityName = draft.entityName;
  output.criterionKey = draft.criterionKey;
  output.originalRequirement = draft.originalRequirement;
  output.originalSuccessCriteria = draft.originalSuccessCriteria;
  output.proposedRequirement = draft.proposedRequirement.trim();
  output.proposedSuccessCriteria = draft.proposedSuccessCriteria.trim();
  output.remarks = draft.remarks.map((remark) => mapToInternalAuditImprovementProposal(remark));

  return output;
}

export function mapFromInternalAuditCriteriaApplyResult(
  dto: InternalAuditCriteriaApplyResultDTO,
): InternalAuditCriteriaApplyResult {
  const output = new InternalAuditCriteriaApplyResult();
  output.applied = (dto?.applied ?? []).map((item) => mapFromInternalAuditCriterionApplied(item));

  return output;
}

function mapFromInternalAuditCriterionApplied(dto: InternalAuditCriterionAppliedDTO): InternalAuditCriterionApplied {
  const output = new InternalAuditCriterionApplied();
  output.entityType = dto.entityType;
  output.entityId = dto.entityId;
  output.entityName = dto.entityName ?? undefined;
  output.oldCriterionKey = dto.oldCriterionKey ?? '';
  output.newCriterionKey = dto.newCriterionKey ?? '';
  output.forked = dto.forked === true;
  output.coveringProcedureNames = dto.coveringProcedureNames ?? [];

  return output;
}

export function mapFromInternalAuditImprovementResult(
  dto: InternalAuditImprovementResultDTO,
): InternalAuditImprovementResult {
  const output = new InternalAuditImprovementResult();
  output.taskId = dto?.taskId ?? undefined;
  output.createdProcedureNames = dto?.createdProcedureNames ?? [];
  output.updatedProcedureNames = dto?.updatedProcedureNames ?? [];
  output.linkedProcedureNames = dto?.linkedProcedureNames ?? [];

  return output;
}
